import * as THREE from 'three';
import { TerrainManager } from './TerrainManager';
import { LaunchHill } from './LaunchHill';
import { MountainPeak } from './MountainPeak';
import { MountainRidge } from './MountainRidge';
import { Hill } from './Hill';
import { CraterBowl } from './CraterBowl';
import { TERRAIN_ZONES } from './TerrainConfig';

/**
 * Generates and places all terrain features according to the map layout plan.
 * Creates strategic terrain for gameplay and procedural hills for atmosphere.
 */
export class TerrainGenerator {
  private scene: THREE.Scene;
  private terrainManager: TerrainManager;

  constructor(scene: THREE.Scene, terrainManager: TerrainManager) {
    this.scene = scene;
    this.terrainManager = terrainManager;
  }

  /**
   * Generate all terrain features.
   */
  generate(): void {
    this.generateLaunchHills();
    this.generateMountainPeaks();
    this.generateMountainRidges();
    this.generateCraterBowls();
    this.generateProceduralHills();
  }

  /**
   * Generate strategic launch hills for Tier 1-2 access.
   * 8 launch hills positioned around the middle zone.
   */
  private generateLaunchHills(): void {
    const launchHillConfigs = [
      // Cardinal directions - larger hills for Tier 2 access
      {
        position: new THREE.Vector3(0, 0, 500),
        rotation: 0, // Facing +Z
        peakHeight: 12,
        length: 40,
        width: 25,
      },
      {
        position: new THREE.Vector3(0, 0, -500),
        rotation: Math.PI, // Facing -Z
        peakHeight: 12,
        length: 40,
        width: 25,
      },
      {
        position: new THREE.Vector3(500, 0, 0),
        rotation: -Math.PI / 2, // Facing +X
        peakHeight: 10,
        length: 35,
        width: 22,
      },
      {
        position: new THREE.Vector3(-500, 0, 0),
        rotation: Math.PI / 2, // Facing -X
        peakHeight: 10,
        length: 35,
        width: 22,
      },

      // Diagonal positions - smaller hills for Tier 1 access
      {
        position: new THREE.Vector3(380, 0, 380),
        rotation: -Math.PI / 4, // Facing NE diagonal highway
        peakHeight: 7,
        length: 30,
        width: 20,
      },
      {
        position: new THREE.Vector3(-380, 0, 380),
        rotation: Math.PI / 4, // Facing NW
        peakHeight: 7,
        length: 30,
        width: 20,
      },
      {
        position: new THREE.Vector3(380, 0, -380),
        rotation: (-3 * Math.PI) / 4, // Facing SE
        peakHeight: 7,
        length: 30,
        width: 20,
      },
      {
        position: new THREE.Vector3(-380, 0, -380),
        rotation: (3 * Math.PI) / 4, // Facing SW
        peakHeight: 7,
        length: 30,
        width: 20,
      },
    ];

    for (const config of launchHillConfigs) {
      const hill = new LaunchHill(this.scene, config);
      this.terrainManager.registerFeature(hill);
    }
  }

  /**
   * Generate mountain peaks at the four corners.
   * Heights: 40-45m for Tier 4 access.
   */
  private generateMountainPeaks(): void {
    const peakConfigs = [
      {
        position: new THREE.Vector3(-700, 0, 700),
        peakHeight: 45,
        baseRadius: 80,
        irregularity: 0.15,
      },
      {
        position: new THREE.Vector3(700, 0, 700),
        peakHeight: 40,
        baseRadius: 70,
        irregularity: 0.18,
      },
      {
        position: new THREE.Vector3(-700, 0, -700),
        peakHeight: 40,
        baseRadius: 70,
        irregularity: 0.12,
      },
      {
        position: new THREE.Vector3(700, 0, -700),
        peakHeight: 45,
        baseRadius: 80,
        irregularity: 0.2,
      },
    ];

    for (const config of peakConfigs) {
      const peak = new MountainPeak(this.scene, config);
      this.terrainManager.registerFeature(peak);
    }
  }

  /**
   * Generate mountain ridges connecting peaks.
   * Heights: 15-25m for Tier 3 access.
   */
  private generateMountainRidges(): void {
    const ridgeConfigs = [
      // North ridge (connecting NW and NE peaks)
      {
        startPoint: new THREE.Vector3(-500, 0, 750),
        endPoint: new THREE.Vector3(500, 0, 750),
        peakHeight: 20,
        width: 40,
      },
      // South ridge (connecting SW and SE peaks)
      {
        startPoint: new THREE.Vector3(-500, 0, -750),
        endPoint: new THREE.Vector3(500, 0, -750),
        peakHeight: 20,
        width: 40,
      },
      // West ridge (connecting NW and SW peaks)
      {
        startPoint: new THREE.Vector3(-750, 0, 500),
        endPoint: new THREE.Vector3(-750, 0, -500),
        peakHeight: 18,
        width: 35,
      },
      // East ridge (connecting NE and SE peaks)
      {
        startPoint: new THREE.Vector3(750, 0, 500),
        endPoint: new THREE.Vector3(750, 0, -500),
        peakHeight: 18,
        width: 35,
      },
    ];

    for (const config of ridgeConfigs) {
      const ridge = new MountainRidge(this.scene, config);
      this.terrainManager.registerFeature(ridge);
    }
  }

  /**
   * Generate crater bowls for speed runs.
   */
  private generateCraterBowls(): void {
    const craterConfigs = [
      // Cardinal crater bowls
      {
        position: new THREE.Vector3(550, 0, 0),
        outerRadius: 45,
        innerRadius: 15,
        rimHeight: 6,
        depth: 2,
      },
      {
        position: new THREE.Vector3(-550, 0, 0),
        outerRadius: 45,
        innerRadius: 15,
        rimHeight: 6,
        depth: 2,
      },
      {
        position: new THREE.Vector3(0, 0, 550),
        outerRadius: 50,
        innerRadius: 18,
        rimHeight: 7,
        depth: 3,
      },
      {
        position: new THREE.Vector3(0, 0, -550),
        outerRadius: 50,
        innerRadius: 18,
        rimHeight: 7,
        depth: 3,
      },
    ];

    for (const config of craterConfigs) {
      const crater = new CraterBowl(this.scene, config);
      this.terrainManager.registerFeature(crater);
    }
  }

  /**
   * Generate procedural gentle hills for scenic variety.
   * Approximately 25 hills placed in the outer zones.
   */
  private generateProceduralHills(): void {
    const hillCount = 25;
    const minDistance = 60; // Minimum distance between hills
    const placedHills: THREE.Vector3[] = [];

    // Seeded random for consistent generation
    const seed = 12345;
    let randomState = seed;
    const random = () => {
      randomState = (randomState * 1103515245 + 12345) & 0x7fffffff;
      return randomState / 0x7fffffff;
    };

    let attempts = 0;
    const maxAttempts = hillCount * 20;

    while (placedHills.length < hillCount && attempts < maxAttempts) {
      attempts++;

      // Generate position in outer zone (avoid city core and middle zone strategic terrain)
      const angle = random() * Math.PI * 2;
      const minRadius = TERRAIN_ZONES.MIDDLE_ZONE + 50;
      const maxRadius = TERRAIN_ZONES.OUTER_ZONE - 100;
      const radius = minRadius + random() * (maxRadius - minRadius);

      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      const position = new THREE.Vector3(x, 0, z);

      // Check minimum distance from other hills
      let tooClose = false;
      for (const placed of placedHills) {
        if (position.distanceTo(placed) < minDistance) {
          tooClose = true;
          break;
        }
      }

      if (tooClose) continue;

      // Avoid placing near mountain peaks (80 unit radius)
      const peakPositions = [
        new THREE.Vector3(-700, 0, 700),
        new THREE.Vector3(700, 0, 700),
        new THREE.Vector3(-700, 0, -700),
        new THREE.Vector3(700, 0, -700),
      ];

      let nearPeak = false;
      for (const peak of peakPositions) {
        if (position.distanceTo(peak) < 120) {
          nearPeak = true;
          break;
        }
      }

      if (nearPeak) continue;

      // Random hill properties
      const height = 2 + random() * 4; // 2-6m
      const hillRadius = 15 + random() * 25; // 15-40m

      const hill = new Hill(this.scene, {
        position,
        height,
        radius: hillRadius,
      });

      this.terrainManager.registerFeature(hill);
      placedHills.push(position);
    }
  }
}

// Re-export terrain components for convenient imports
export { TerrainManager } from './TerrainManager';
export { TerrainFeature } from './TerrainFeature';
export { LaunchHill } from './LaunchHill';
export { MountainPeak } from './MountainPeak';
export { MountainRidge } from './MountainRidge';
export { Hill } from './Hill';
export { CraterBowl } from './CraterBowl';
export * from './TerrainConfig';
